import { Form, Modal, Select, message } from 'antd';
import { isAxiosError } from 'axios';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Course from '../../course/types/Course';
import OpenClassAPI from '../OpenClassAPI';
import { useCourseCertificateTemplates } from '../hooks/useCourseCertificateTemplates';
import { OpenClass, UpdateOpenClassParams } from '../types/OpenClass';
import OpenClassStatusBadge from './OpenClassStatusBadge';
import { certificateTemplateOptions } from '../utils/openClass';

type OpenClassEditModalProps = {
  open: boolean;
  openClass: OpenClass;
  courses: Course[];
  onClose: () => void;
  onSaved: () => void;
};

type EditFormValues = {
  certificateTemplateIds: number[];
  courseId: number;
};

const OpenClassEditModal: React.FC<OpenClassEditModalProps> = ({
  open,
  openClass,
  courses,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<EditFormValues>();
  const watchedCourseId = Form.useWatch('courseId', form);
  const {
    templates: certificateTemplates,
    loading: loadingCertificateTemplates,
  } = useCourseCertificateTemplates(watchedCourseId, open);

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        certificateTemplateIds: openClass.certificateTemplates.map(
          ({ id }) => id,
        ),
        courseId: openClass.course.id,
      });
    }
  }, [form, open, openClass]);

  const submit = useCallback(
    async (values: EditFormValues) => {
      const params: UpdateOpenClassParams = {
        certificateTemplateIds: values.certificateTemplateIds,
        courseId: values.courseId,
      };

      try {
        await OpenClassAPI.updateOpenClass(openClass.id, params);
        onSaved();
        onClose();
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 409) {
          message.error(t('views.OpenClasses.certificateOfferingInUse'));
        } else if (isAxiosError(error) && !error.response?.data.message) {
          message.error(t('views.OpenClasses.saveFailed'));
        }
      }
    },
    [onClose, onSaved, openClass.id, t],
  );

  return (
    <Modal
      destroyOnClose
      open={open}
      title={
        // The status is derived from the sessions now, so it is shown rather than chosen.
        <div className="flex items-center gap-3">
          {t('views.OpenClasses.editOpenClass')}
          <OpenClassStatusBadge
            endAt={openClass.endAt}
            status={openClass.status}
          />
        </div>
      }
      onCancel={onClose}
      onOk={form.submit}>
      <Form form={form} layout="vertical" onFinish={submit}>
        <Form.Item
          label={t('common.course')}
          name="courseId"
          rules={[{ required: true }]}>
          <Select
            optionFilterProp="label"
            showSearch
            options={courses.map(({ id, name }) => ({
              label: name,
              value: id,
            }))}
            onChange={() => form.setFieldValue('certificateTemplateIds', [])}
          />
        </Form.Item>
        <Form.Item
          label={t('views.OpenClasses.certificateTypes')}
          name="certificateTemplateIds"
          rules={[{ required: true }]}>
          <Select
            mode="multiple"
            disabled={watchedCourseId == null || loadingCertificateTemplates}
            loading={loadingCertificateTemplates}
            options={certificateTemplateOptions(certificateTemplates)}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default OpenClassEditModal;
